import theme from "@/styles/theme";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { NoDataFound } from "@/components/NoDataFound";
import { useWorkshops } from "@/lib/api";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { grey } from "@mui/material/colors";
import { ContentContainer } from "@/components/ContentContainer";
import { CollapseImageLeftTextRight } from "@/components/CollapseImageLeftTextRight";
import { Box, Fade } from "@mui/material";
import { WorkshopEntry } from "@/components/workshops/WorkshopEntry";
import React, { useEffect, useState } from "react";
import Error from "@/pages/_error";
import { WorkshopFilterContainer } from "@/components/workshops/WorkshopFilterContainer";
import { SkeletonStyled } from "@/components/SkeletonStyled";
import { CircularProgressStyled } from "@/components/CircularProgressStyled";
import PageHead from "@/components/PageHead";

export default function WorkshopsIndex({ page, ref, ...props }) {
  const [filterConfig, setFilterConfig] = useState({
    date: new Date().toISOString(),
  });
  const { workshops, isLoading, isValidating, isError, mutate } =
    useWorkshops(filterConfig);
  const [workshopsToDisplay, setWorkshopsToDisplay] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setWorkshopsToDisplay(workshops);
  }, [workshops]);

  useEffect(() => {
    mutate();
  }, [filterConfig]);

  const handleFilterChanged = (event) => {
    const re = new RegExp(event.target.value, "gi");
    const filteredWorkshops = workshops.filter((w) => {
      return w.title.match(re);
    });
    setWorkshopsToDisplay(filteredWorkshops);
  };

  const handleInputChanged = (event, value) => {
    const newConfig = { ...filterConfig };
    if (!event.target.value) {
      delete newConfig[event.target.name];
    }

    newConfig[event.target.name] = value ?? event.target.value?.id;

    setFilterConfig(newConfig);
  };

  const handleOnlineOfflineChanged = (event) => {
    const newConfig = { ...filterConfig };
    newConfig["online"] = event.target.value === "online" ? true : false;

    setFilterConfig(newConfig);
  };

  if (isError) return <Error />;

  const style = {
    "& .spinner": {
      height: "18px",
      width: "18px",
    },
  };

  return (
    <>
      <PageHead page={page} />

      <ContentContainer
        headline="Workshops"
        image={page?.hero_image?.formats?.large?.url}
      >
        <Grid container sx={style}>
          <Grid item xs={12}>
            <Grid container spacing={{ xs: 1, sm: 3 }}>
              <Grid
                item
                xs={12}
                sx={{
                  display: { xs: "block", sm: "none" },
                }}
              >
                {filterOpen ? (
                  <Fade in={filterOpen}>
                    <Box ref={ref} {...props}>
                      <WorkshopFilterContainer
                        filterMobile={true}
                        onCancel={() => setFilterOpen(false)}
                        handleInputChanged={handleInputChanged}
                        handleFilterChanged={handleFilterChanged}
                        handleOnlineOfflineChanged={handleOnlineOfflineChanged}
                        filterOpen
                      />
                    </Box>
                  </Fade>
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    startIcon={<FilterAltIcon />}
                    onClick={() => setFilterOpen(true)}
                  >
                    Filter anzeigen
                  </Button>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  display: { xs: "none", sm: "block" },
                  mb: 4,
                  "& .MuiAutocomplete-root:not(:last-of-type)": {
                    mb: 1,
                  },
                }}
              >
                <Typography variant="h4" sx={{ mb: 1 }}>
                  Filter
                </Typography>
                <WorkshopFilterContainer
                  handleInputChanged={handleInputChanged}
                  handleFilterChanged={handleFilterChanged}
                  handleOnlineOfflineChanged={handleOnlineOfflineChanged}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={1}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      [theme.breakpoints.up("sm")]: { mb: "-11px" },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        display: { xs: "none", sm: "block" },
                        mb: 1,
                      }}
                    >
                      Workshops
                    </Typography>
                    <Grid
                      container
                      alignItems="center"
                      sx={{
                        [theme.breakpoints.up("sm")]: {
                          mt: "-.625rem",
                        },
                      }}
                    >
                      <Grid item xs={8} sx={{ mb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            textTransform: "uppercase",
                            color: grey[600],
                          }}
                        >
                          <>
                            {isValidating ? (
                              <>
                                <CircularProgressStyled /> Workshops gefunden
                              </>
                            ) : (
                              <>
                                {workshopsToDisplay?.length} Workshops gefunden
                              </>
                            )}
                          </>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    {isLoading ? (
                      <SkeletonStyled type="rectangle" />
                    ) : (
                      <>
                        {(!workshopsToDisplay ||
                          workshopsToDisplay.length === 0) && (
                          <NoDataFound type="Workshops" />
                        )}

                        {workshopsToDisplay?.map((workshop) => (
                          <>
                            <CollapseImageLeftTextRight key={workshop.id}>
                              <WorkshopEntry workshop={workshop} />
                            </CollapseImageLeftTextRight>
                          </>
                        ))}
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </ContentContainer>
    </>
  );
}

export const getStaticProps = async () => {
  const pageResponse = await fetch(
    `${
      process.env.NEXT_PUBLIC_API_URL || "http://localhost:1337"
    }/page-workshops`
  );
  const page = await pageResponse.json();

  return {
    props: {
      page,
    },
    revalidate: parseInt(process.env.SSG_REVALIDATE_TIMEOUT),
  };
};
